import { z } from 'zod';

import { zGoDate, zGoSlice } from '../helpers/fix-go-types';
import { AddressSchema } from './address';
import { OrderSettingsSchema } from './order-settings';
import { RepresentativeSchema } from './representative';

export const VendorOrderingSchema = z.object({
  send_methods: z.array(z.string()),
  default_send_method: z.string(),
  download_file_format: z.string(),
});

export const VendorReceivingSchema = z.object({
  invoice_formats: z.array(z.string()),
});

export const VendorPricingSchema = z.object({
  sources: z.array(z.string()),
  has_volume_pricing: z.boolean(),
});

export const VendorIntegrationSchema = z.object({
  normalized_name: z.string(),
  label: z.string(),
  state: z.string(),
  ordering: VendorOrderingSchema,
  receiving: VendorReceivingSchema.nullish().default(null),
  pricing: VendorPricingSchema.nullish().default(null),
});

export type VendorIntegration = z.infer<typeof VendorIntegrationSchema>;

// form validation rules
export const VendorSchema = z.object({
  vendor_id: z.string(),
  store_id: z.string(),
  name: z.string(),
  normalized_name: z.string(),
  account_number: z.string(),
  terms: z.string(),
  notes: z.string(),
  // `InvoiceFormats []string` carries no omitempty (models/vendors.go:139), so
  // a vendor with no parser arrives with null here and used to fail validation
  // outright — the whole vendor dropped, not just the field.
  invoice_formats: zGoSlice(z.string()),
  accepts_ai_csv: z.boolean(),
  order_settings: OrderSettingsSchema,
  address: AddressSchema.default({}),
  disabled: z.boolean(),

  updated_at: zGoDate,
});

export type Vendor = z.infer<typeof VendorSchema>;

// react-hook-form types a form on its resolver schema's INPUT, not its output.
// VendorSchema's input deliberately accepts the raw Go shapes — a nil slice for
// `representatives`, an RFC3339 string for `updated_at` — which leaves those
// fields typed `unknown`, and a field path like
// `order_settings.representatives.0.email` cannot be walked through `unknown`.
//
// The vendor edit form edits an already-parsed vendor, so it validates the same
// rules stated over the parsed array. Only `representatives` needs restating:
// it is the one fixed-up field the form registers into. Parsing is unchanged —
// the wire still goes through VendorSchema.
export const VendorFormSchema = VendorSchema.extend({
  order_settings: OrderSettingsSchema.extend({
    representatives: z.array(RepresentativeSchema),
  }),
});

// A vendor with no native invoice parser that instead goes through the AI-CSV
// receiving flow. accepts_ai_csv with a non-empty invoice_formats is
// intentionally not generic — native parsers win.
export const isGenericAiVendor = (vendor: Vendor): boolean =>
  vendor.accepts_ai_csv && vendor.invoice_formats.length === 0;

// Temporary (ORD-567): RBG's Oklahoma vendors carry the RNDC_OK integration —
// the key predates RBG's takeover of RNDC.
export const isRBGVendor = (vendor: Vendor): boolean =>
  vendor.normalized_name === 'RNDC_OK' && !vendor.disabled;

// normalized_name -> the label the Vendors page shows for that integration.
// Anywhere a group of sibling vendors is named for its integration, look the
// name up here: normalized_name is an internal code ('ARMA_OK') that stores
// never see anywhere else in the app.
export const integrationLabels = (integrations: VendorIntegration[]): Map<string, string> =>
  new Map(integrations.map((i) => [i.normalized_name, i.label]));
